import React, { type ReactNode } from 'react';
import * as R from 'ramda';
import WarningIcon from '@atlaskit/icon/glyph/warning';
import { token } from '@atlaskit/tokens';
import { AkFlag as Flag } from '@atlassian/jira-flags';
import { useIntl } from '@atlassian/jira-intl';
import { colors } from '@atlassian/jira-portfolio-3-common';
import type { DateConfiguration } from '@atlassian/jira-portfolio-3-portfolio/src/app-simple-plans/query/plan';
import { useRedirectToSpaPlanPage } from '@atlassian/jira-portfolio-3-portfolio/src/app-simple-plans/view/redirect-to-spa-plan-page';
import {
	DATEFIELD_WARNINGS,
	type DateField,
} from '@atlassian/jira-portfolio-3-portfolio/src/common/api/types';
import commonMessages from '@atlassian/jira-portfolio-3-portfolio/src/common/view/messages';
import messages from './messages';
import type { Props } from './types';

function ConfiguredDatesUnavailableFlag({ dateConfiguration, handleKeepUsingTargetDates }: Props) {
	const { formatMessage } = useIntl();
	const dateFieldsWithWarning = R.pickBy<DateConfiguration, DateField>(
		(field) => !!field.warning,
		dateConfiguration,
	);
	const numFieldsDontExist = Object.keys(
		R.pickBy(R.pathEq(['warning', 'code'], DATEFIELD_WARNINGS.NOT_EXIST), dateConfiguration),
	).length;

	const startDateTitle: string | undefined = R.path(
		['baselineStartField', 'warning', 'apiDateField', 'title'],
		dateFieldsWithWarning,
	);

	const endDateTitle: string | undefined = R.path(
		['baselineEndField', 'warning', 'apiDateField', 'title'],
		dateFieldsWithWarning,
	);

	const redirectToSpaPlanPage = useRedirectToSpaPlanPage();

	let description;

	if (numFieldsDontExist) {
		description = formatMessage(commonMessages.chooseConfigurableDateNotExistContent, {
			count: numFieldsDontExist,
		});
	} else if (startDateTitle) {
		description = endDateTitle
			? formatMessage(commonMessages.chooseConfigurableDatesUnavailableContentStartAndEndDate, {
					startDateTitle,
					endDateTitle,
					// eslint-disable-next-line @atlaskit/design-system/use-primitives-text
					strong: (chunks: ReactNode) => <strong>{chunks}</strong>,
				})
			: formatMessage(commonMessages.chooseConfigurableDatesUnavailableContentStartDate, {
					startDateTitle,
					// eslint-disable-next-line @atlaskit/design-system/use-primitives-text
					strong: (chunks: ReactNode) => <strong>{chunks}</strong>,
				});
	} else if (endDateTitle) {
		description = formatMessage(commonMessages.chooseConfigurableDatesUnavailableContentEndDate, {
			endDateTitle,
			// eslint-disable-next-line @atlaskit/design-system/use-primitives-text
			strong: (chunks: ReactNode) => <strong>{chunks}</strong>,
		});
	}

	const actions = [
		{
			content: formatMessage(messages.useOtherDates),
			onClick: () => {
				redirectToSpaPlanPage('/settings/scheduling');
			},
		},
		{
			content: formatMessage(messages.keepUsingTargetDates),
			onClick: handleKeepUsingTargetDates,
		},
	];

	return (
		<Flag
			actions={actions}
			icon={<WarningIcon label="" primaryColor={token('color.icon.warning', colors.Y300)} />}
			id="portfolio-3-portfolio.app-simple-plans.main.tabs.roadmap.configured-dates-flag.flag"
			description={description}
			title={formatMessage(commonMessages.chooseConfigurableDatesUnavailableHeading, {
				count: Object.keys(dateFieldsWithWarning).length,
			})}
		/>
	);
}

export default ConfiguredDatesUnavailableFlag;
